import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, ViewChild, AfterViewInit, Output, EventEmitter, SimpleChanges, OnChanges, SimpleChange, OnInit } from '@angular/core';

import { Subscription } from 'rxjs';

import { jqxButtonModule } from 'jqwidgets-ng/jqxbuttons';
import { jqxWindowComponent, jqxWindowModule } from 'jqwidgets-ng/jqxwindow';
import { LoaderComponent } from '../loader/loader.component';
import { WindowActionButton } from '../../models/index';
import { ButtonComponent } from '../button/button.component';

const MINIMIZED_WINDOW_WIDTH = 150;
const WINDOW_HEADER_HEIGHT = 30;
const TABS_GAP = 5;

const DEFAULT_WINDOW_WIDTH = 800;
const DEFAULT_WINDOW_HEIGHT = 600;
const DEFAULT_WINDOW_MARGIN = 50;

const MAX_WINDOW_PER_ROW = 4;

@Component({
  selector: 'mcg-window',
  standalone: true,
  templateUrl: './window.component.html',
  styleUrl: './window.component.scss',
  imports: [
    CommonModule,
    jqxWindowModule,
    jqxButtonModule,
    LoaderComponent,
    ButtonComponent
  ],
})
export class WindowComponent implements OnDestroy, AfterViewInit, OnChanges, OnInit {
  @ViewChild(jqxWindowComponent)
  public window: jqxWindowComponent;

  @Input()
  public loading: boolean = false;

  @Input()
  public title = '';

  @Input()
  public minWidth = DEFAULT_WINDOW_WIDTH;

  @Input()
  public minHeight = DEFAULT_WINDOW_HEIGHT;

  @Input()
  public width;

  @Input()
  public height;

  @Input()
  public shouldOpen=true;

  @Input()
  public canResize = true;

  @Input()
  public isDraggable = true;

  @Input()
  public actions: WindowActionButton[] = null;

  @Input()
  public containerSelector = '#map-container > .tabcontent';

  @Output()
  public windowOpened = new EventEmitter();

  @Output()
  public windowClosed = new EventEmitter();

  @Output()
  public windowExpanded = new EventEmitter()

  @Output()
  public windowCollapsed = new EventEmitter()

  @Output()
  public actionButtonClicked = new EventEmitter<unknown>();

  private isMaximized: boolean = true;
  private containerObject = null;
  private windowEventsSubscriptions: Subscription[];
  private resizeObserver: ResizeObserver;
  public static window_count = 0;

  private static collapsedTabs: WindowComponent[] = [];
  private static windows: WindowComponent[] = [];

  public constructor() {}

  public ngOnChanges(changes: SimpleChanges): void {
    Object.entries(changes).forEach(([propName, changedProperty]: [string, SimpleChange]) => {
      const { currentValue } = changedProperty;

      switch(propName) {
        case 'containerSelector':
          if (!currentValue) {
            return;
          }

          this.containerObject = document.querySelector(currentValue);
        break;

        case 'actions':
          if(!currentValue) {
            return;
          }
      }
    });
  }

  public ngOnInit(): void {
    const el = document.querySelector(this.containerSelector);
    this.containerObject = el instanceof HTMLElement ? el : null;

    if (this.containerObject) {
      const containerRect = this.containerObject.getBoundingClientRect();

      if (!this.width) {
        this.width = containerRect.width;
      }

      if (!this.height) {
        this.height = containerRect.height;
      }

      if(this.width && this.height) {
        this.isMaximized = false;
      }
    }
  }

  public ngAfterViewInit(): void {
    this.positionWindow();
    this.addToWindows();

    if(!this.containerObject) {
      return;
    }

    this.adjustWindowZIndex();

    this.window.resizable(this.canResize);
    this.window.draggable(this.isDraggable);

    this.registerWindowEvents();
    this.registerResizeObserver();

    this.expand();
  }

  public positionWindow():void {
    const containerRect = this.containerObject.getBoundingClientRect();
    const x = containerRect.left;
    const y = containerRect.top;

    const col = WindowComponent.window_count % MAX_WINDOW_PER_ROW;

    const posX = col * DEFAULT_WINDOW_MARGIN + x;
    const posY = col * DEFAULT_WINDOW_MARGIN + y;

    this.setPosition(posX, posY);

    WindowComponent.window_count++;
  }

  private adjustWindowZIndex(): void {
    const el = this.window?.host?.[0];
    if (!el) return;

    const z = parseInt(el.style.zIndex || '0', 10);
    if (z >= 998) el.style.zIndex = `${z - 1}`;
  }

  public collapseWindow(): void {
    this.window.collapse();
  }

  public expandWindow(): void {
    this.window.expand();
  }

  public bringToFront(): void {
    this.window.bringToFront();
  }

  public closeWindow(): void {
    this.window.close();
  }

  public openWindow(): void{
    this.window.open();
  }

  public enableWindow(): void {
    this.window.enable();
  }

  public disableWindow(): void {
    this.window.disable();
  }

  public ngOnDestroy(): void {
    if(!this.window) {
      return;
    }

    this.window.close();

    if(!this.containerObject) {
      return;
    }

    this.resizeObserver.disconnect();
    this.windowEventsSubscriptions.forEach(s => s.unsubscribe());

    if (this.window.collapsed()) {
      this.removeFromCollapsedWindows();
      this.updateCollapsedWindows();
    }

    this.removeFromWindows();
  }

  private createActionButtonFrom(button): unknown{
    const actionForm = {
      action: button.action,
      value: button.value,
      type: button.type
    }
    return actionForm;
  }

  private registerWindowEvents(): void {
    this.windowEventsSubscriptions = [
      this.window.onResizing.subscribe(() => {
        this.isMaximized = false;
      }),

      this.window.onMoved.subscribe(() => {
        if (this.isMaximized) {
          this.expand();
        }
      }),

      this.window.onCollapse.subscribe(() => {
        this.windowCollapsed.emit();
        this.collapse(WindowComponent.collapsedTabs.length);
        WindowComponent.setFocusOnPreviousWindow();

        this.addAsCollapsedWindow();
      }),

      this.window.onExpand.subscribe(() => {
        this.windowExpanded.emit();
        this.expand();
        this.removeFromCollapsedWindows();
        this.updateCollapsedWindows();
      }),

      this.window.onClose.subscribe(() => {
        this.windowClosed.emit();
      }),
    ]
  }

  private registerResizeObserver(): void {
    this.resizeObserver = new ResizeObserver(() => {
      if (this.isMaximized) {
        this.setMaxSize(window.innerWidth, window.innerHeight)
        this.expand();
      }
      this.updateCollapsedWindows();
    });
    this.resizeObserver.observe(this.containerObject);
  }

  private addAsCollapsedWindow(): void {
    WindowComponent.collapsedTabs.push(this);
  }

  private removeFromCollapsedWindows(): void {
    WindowComponent.collapsedTabs = WindowComponent.collapsedTabs.filter(window => window !== this);
  }

  private updateCollapsedWindows(): void {
    WindowComponent.collapsedTabs.forEach((window, idx) => window.collapse(idx));
  }

  private addToWindows(): void {
    WindowComponent.windows.push(this);
  }

  private removeFromWindows(): void {
    WindowComponent.windows = WindowComponent.windows.filter(window => window !== this);
  }

  private collapse(collapsedIndex): void {
    const containerRect = this.containerObject.getBoundingClientRect();
    const x = containerRect.left + (MINIMIZED_WINDOW_WIDTH + TABS_GAP) * collapsedIndex;
    const y = containerRect.bottom - WINDOW_HEADER_HEIGHT;

    this.window.minWidth(MINIMIZED_WINDOW_WIDTH);
    this.window.width(MINIMIZED_WINDOW_WIDTH);
    this.setPosition(x, y);

    this.window.resizable(false);
    this.window.draggable(false);
  }

  private expand(): void {
    const containerRect = this.containerObject.getBoundingClientRect();
    const x = containerRect.left;
    const y = containerRect.top;

    this.setPosition(x, y);
    this.window.resizable(this.canResize);
    this.window.draggable(this.isDraggable);

    if (!this.canResize) {
      this.setSize(this.width, this.height);
      return;
    }

    this.setMaxSize(window.innerWidth, window.innerHeight);
    this.setSize(containerRect.width, containerRect.height);
    this.isMaximized = true;
  }

  private setSize(width: number, height: number): void {
    this.window.width(width);
    this.window.height(height);
  }

  private setMaxSize(maxWidth: number, maxHeight: number): void {
    this.window.maxWidth(maxWidth);
    this.window.maxHeight(maxHeight);
  }

  public setPosition(x: number, y: number): void {
    this.window.position({ x, y });
  }

  private static setFocusOnPreviousWindow(): void {
    WindowComponent.windows.find(w => !w.window.collapsed())?.window.focus();
  }
}
